/**
 * Central SEO + brand config for Ctxor.
 * Pronunciation: CON-sor (Thai: คอน-ซอร์) — from “Context”.
 */

export const SITE_NAME = 'Ctxor'

/** Tagline — primary brand line. */
export const SITE_TAGLINE = 'Context for every AI.'

/** How to say the name (branding / a11y / llms). */
export const SITE_PRONUNCIATION = 'CON-sor'

/** Absolute public origin. Resolves per-environment; falls back to prod. */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_BASE_URL || 'https://ctxor.com'
).replace(/\/+$/, '')

export const SITE_TITLE_DEFAULT = `Ctxor — ${SITE_TAGLINE}`

/** Child pages set `title: "X"` and render as "X | Ctxor". */
export const SITE_TITLE_TEMPLATE = '%s | Ctxor'

export const SITE_DESCRIPTION =
  'Ctxor is the marketplace for AI context — prompts, context packs, skills, agents, and workflows. ' +
  'Discover, buy, and sell expert-crafted context for ChatGPT, Midjourney, Gemini, Claude, and more.'

export const SITE_KEYWORDS = [
  'Ctxor',
  'AI context',
  'context packs',
  'AI prompts',
  'prompt marketplace',
  'ChatGPT prompts',
  'Midjourney prompts',
  'agent skills',
  'AI workflows',
  'prompt engineering',
]

/** Brand clover mark in /public (also the favicon). */
export const SITE_LOGO_PATH = '/icons/clover.svg'
export const SITE_LOGO = `${SITE_URL}${SITE_LOGO_PATH}`

/** schema.org sameAs — add live profiles when ready. */
export const SITE_SOCIAL_LINKS: string[] = [
  // 'https://x.com/ctxor',
  // 'https://github.com/ctxor',
  // 'https://discord.gg/ctxor',
]

export const SITE_CONTACT_EMAIL = 'support@ctxor.com'

/** Sitelinks search box target (schema.org SearchAction). */
export const SITE_SEARCH_URL = `${SITE_URL}/marketplace/`

/** Default offer currency. */
export const SITE_CURRENCY = 'USD'

/**
 * Serialize JSON-LD safely for inline <script>: escape `<` so a stray
 * `</script>` can never terminate the script element.
 */
export function jsonLdStringify(data: Record<string, unknown>): string {
  return JSON.stringify(data).replace(/</g, '\\u003c')
}

/** Minimal prompt shape needed to build Product schema. */
export interface ProductSchemaInput {
  documentId: string
  name: string
  description: string
  price: number | string
  slug?: string
  banner_url?: string
  category?: string
  author_name?: string
  discount_percent?: number | string | null
  discount_start_at?: string | null
  discount_end_at?: string | null
  example_outputs?: Array<{ type?: string; url?: string }>
}

function activeDiscountPercent(p: ProductSchemaInput): number {
  const pct = Number(p.discount_percent) || 0
  if (pct <= 0) return 0
  const now = Date.now()
  if (p.discount_start_at && new Date(p.discount_start_at).getTime() > now) return 0
  if (p.discount_end_at && new Date(p.discount_end_at).getTime() < now) return 0
  return Math.min(Math.max(pct, 0), 100)
}

function firstImage(p: ProductSchemaInput): string | undefined {
  if (p.banner_url) return p.banner_url
  const ex = Array.isArray(p.example_outputs)
    ? p.example_outputs.find((o) => o?.type === 'image' && o?.url)
    : undefined
  return ex?.url
}

/** Absolute URL helper for a site path (may include query string). */
export function absoluteUrl(path: string): string {
  if (!path || path === '/') return SITE_URL
  if (/^https?:\/\//i.test(path)) return path
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

export function buildOrganizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    alternateName: ['Ctxor AI', 'CON-sor'],
    slogan: SITE_TAGLINE,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: SITE_LOGO,
    },
    description: SITE_DESCRIPTION,
    email: SITE_CONTACT_EMAIL,
    ...(SITE_SOCIAL_LINKS.length > 0 ? { sameAs: SITE_SOCIAL_LINKS } : {}),
  }
}

export function buildWebSiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: SITE_DESCRIPTION,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: SITE_LOGO },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_SEARCH_URL}{search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

export function buildWebPageJsonLd(opts: {
  path: string
  title: string
  description?: string
  type?: string
}): Record<string, unknown> {
  const url = absoluteUrl(opts.path)
  return {
    '@context': 'https://schema.org',
    '@type': opts.type ?? 'WebPage',
    name: opts.title,
    url,
    ...(opts.description ? { description: opts.description } : {}),
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  }
}

export function buildBreadcrumbJsonLd(
  items: Array<{ name: string; path: string }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  }
}

export function buildFaqPageJsonLd(
  faqs: Array<{ question: string; answer: string }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

export function buildItemListJsonLd(opts: {
  name: string
  description?: string
  path: string
  items: Array<{ name: string; path: string }>
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: opts.name,
    ...(opts.description ? { description: opts.description } : {}),
    url: absoluteUrl(opts.path),
    numberOfItems: opts.items.length,
    itemListElement: opts.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: absoluteUrl(item.path),
    })),
  }
}

export function buildContactPageJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact',
    url: absoluteUrl('/contact'),
    description: `Contact ${SITE_NAME} support`,
    mainEntity: {
      '@type': 'Organization',
      name: SITE_NAME,
      email: SITE_CONTACT_EMAIL,
      url: SITE_URL,
    },
  }
}

/**
 * Build schema.org Product + Offer JSON-LD for a prompt detail page.
 */
export function buildProductJsonLd(
  prompt: ProductSchemaInput,
  opts?: { sellerName?: string },
): Record<string, unknown> {
  const slug = prompt.slug ?? prompt.documentId
  const url = `${SITE_URL}/prompt/${slug}`
  const image = firstImage(prompt)
  const basePrice = Number(prompt.price) || 0
  const discount = activeDiscountPercent(prompt)
  const price = (basePrice * (1 - discount / 100)).toFixed(2)
  const brandName = prompt.author_name || opts?.sellerName

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: prompt.name,
    description: prompt.description,
    sku: prompt.documentId,
    ...(image ? { image: [image] } : {}),
    ...(prompt.category ? { category: prompt.category } : {}),
    ...(brandName ? { brand: { '@type': 'Brand', name: brandName } } : {}),
    offers: {
      '@type': 'Offer',
      url,
      price,
      priceCurrency: SITE_CURRENCY,
      availability: 'https://schema.org/InStock',
    },
  }
}
